import React from 'react';
import type { EvaluationMode } from '../../types/evaluation';
import { EVALUATION_MODE_LABELS } from '../../types/evaluation';
import type { EvaluationJob } from '../../types/evaluationJob';
import {
  canCancelEvaluationJob,
  canRetryEvaluationJob,
  isActiveEvaluationJobStatus,
  isTerminalEvaluationJobStatus,
  resolveEvaluationJobProgressDisplay,
  resolveEvaluationJobDisplayStatus,
  resolveEvaluationJobRawStatus,
  formatEvaluationJobDisplayStatusLabel,
} from '../../utils/evaluationJobProgress';
import { getJobCancelButtonLabel, isJobCancellationPending } from '../../utils/jobActionUi';
import FailedJobErrorMessage from '../ui/FailedJobErrorMessage';
import { resolveEvaluationJobError } from '../../utils/errorUtils';
import TuningModalShell from '../TuningModalShell';
import EvaluationJobProgressBar from './EvaluationJobProgressBar';
import EvaluationRunResultView from './EvaluationRunResultView';
import '../TuningModalShell.css';
import './EvaluationRunResultView.css';

export interface EvaluationJobDetailsModalProps {
  open: boolean;
  onClose: () => void;
  job: EvaluationJob | null;
  loading?: boolean;
  /** Smoothed progress when this job is actively tracked on the page. */
  trackedProgressPercent?: number;
  isTrackedActive?: boolean;
  onCancel?: (jobId: string) => void;
  onRetry?: (jobId: string) => void;
  onRunAgain?: (job: EvaluationJob) => void;
  cancellingId?: string | null;
  retryingRowKey?: string | null;
  isAnyJobRunning?: boolean;
}

function formatDate(value: string | undefined): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

const EvaluationJobDetailsModal: React.FC<EvaluationJobDetailsModalProps> = ({
  open,
  onClose,
  job,
  loading = false,
  trackedProgressPercent,
  isTrackedActive = false,
  onCancel,
  onRetry,
  onRunAgain,
  cancellingId = null,
  retryingRowKey = null,
  isAnyJobRunning = false,
}) => {
  if (!open) return null;

  const evalStatus = resolveEvaluationJobRawStatus(job);
  const evalDisplayStatus = job ? resolveEvaluationJobDisplayStatus(job) : '';
  const evalModeRaw = String(job?.evaluation_mode || '');
  const evalModeLabel =
    evalModeRaw && evalModeRaw in EVALUATION_MODE_LABELS
      ? EVALUATION_MODE_LABELS[evalModeRaw as EvaluationMode]
      : evalModeRaw || 'Evaluation';
  const progressMeta = job ? resolveEvaluationJobProgressDisplay(job) : null;
  const displayPercent =
    isTrackedActive && trackedProgressPercent != null
      ? trackedProgressPercent
      : progressMeta?.percent ?? 0;
  const canRunAgain = isTerminalEvaluationJobStatus(evalStatus);
  const jobId = job?.id?.trim() || '';
  const detailsRetryRowKey = jobId ? `retry-ui:details:${jobId}` : '';
  const isRetrying = Boolean(detailsRetryRowKey && retryingRowKey === detailsRetryRowKey);
  const errorDisplay = job?.error ? resolveEvaluationJobError(job) : null;

  const title =
    evalStatus === 'succeeded'
      ? 'Evaluation completed'
      : evalStatus === 'failed'
        ? 'Evaluation failed'
        : evalStatus === 'cancelled'
          ? 'Evaluation cancelled'
          : evalDisplayStatus === 'queued'
            ? 'Evaluation in queue'
            : isActiveEvaluationJobStatus(evalStatus)
              ? 'Evaluation in progress'
              : 'Evaluation job details';

  const iconClassName =
    evalStatus === 'succeeded'
      ? 'fas fa-check-circle'
      : evalStatus === 'failed'
        ? 'fas fa-times-circle'
        : 'fas fa-chart-line';

  return (
    <TuningModalShell
      onClose={onClose}
      title={title}
      subtitle={`${evalModeLabel}${jobId ? ` · ${jobId}` : ''}`}
      iconClassName={iconClassName}
      size="result"
      variant="eval"
      footer={
        <>
          <button type="button" className="tuning-modal__btn tuning-modal__btn--secondary" onClick={onClose}>
            Close
          </button>
          {jobId && canRunAgain && onRunAgain && job ? (
            <button
              type="button"
              className="tuning-modal__btn tuning-modal__btn--secondary"
              onClick={() => onRunAgain(job)}
              disabled={isAnyJobRunning}
            >
              Run again
            </button>
          ) : null}
          {jobId && canRetryEvaluationJob(evalStatus) && onRetry ? (
            <button
              type="button"
              className="tuning-modal__btn tuning-modal__btn--secondary"
              onClick={() => onRetry(jobId)}
              disabled={isRetrying || isAnyJobRunning}
              aria-busy={isRetrying}
            >
              {isRetrying ? 'Retrying…' : 'Retry'}
            </button>
          ) : null}
          {jobId && canCancelEvaluationJob(evalStatus) && onCancel ? (() => {
            const isCancelPending = isJobCancellationPending(jobId, cancellingId, evalStatus);
            return (
            <button
              type="button"
              className="tuning-modal__btn tuning-modal__btn--secondary"
              onClick={() => onCancel(jobId)}
              disabled={isCancelPending}
              aria-busy={isCancelPending}
            >
              {getJobCancelButtonLabel(isCancelPending, 'Cancel job')}
            </button>
            );
          })() : null}
        </>
      }
    >
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}>
          <div className="spinner spinner--small" />
          <span>Loading job details…</span>
        </div>
      ) : !job ? (
        <p className="evaluator-params__hint">Could not load evaluation job details.</p>
      ) : (
        <>
          <div
            className="training-job-history-item-details"
            style={{ marginBottom: 16, display: 'grid', gap: 8 }}
          >
            <div className="training-job-history-item-detail">
              <span className="detail-label">Status</span>
              <span className="detail-value" style={{ textTransform: 'capitalize' }}>
                {formatEvaluationJobDisplayStatusLabel(evalDisplayStatus)}
              </span>
            </div>
            <div className="training-job-history-item-detail">
              <span className="detail-label">Created</span>
              <span className="detail-value">{formatDate(job.created_at)}</span>
            </div>
            {job.completed_at ? (
              <div className="training-job-history-item-detail">
                <span className="detail-label">Completed</span>
                <span className="detail-value">{formatDate(job.completed_at)}</span>
              </div>
            ) : null}
            {job.dataset_split ? (
              <div className="training-job-history-item-detail">
                <span className="detail-label">Dataset split</span>
                <span className="detail-value">{String(job.dataset_split)}</span>
              </div>
            ) : null}
          </div>

          {(isActiveEvaluationJobStatus(evalStatus) || evalStatus === 'succeeded') && progressMeta ? (
            <div style={{ marginBottom: 16 }}>
              <EvaluationJobProgressBar
                percent={displayPercent}
                message={progressMeta.message}
                meta={progressMeta.meta}
                title="Evaluation progress"
                accentColor="#0099FA"
              />
            </div>
          ) : null}

          {evalStatus === 'failed' && errorDisplay ? (
            <div style={{ marginBottom: 16 }}>
              <FailedJobErrorMessage
                rawError={errorDisplay.raw}
                friendlyMessage={errorDisplay.friendly}
                details={errorDisplay.details}
                fallbackMessage="Evaluation job failed"
                compact
              />
            </div>
          ) : null}

          {evalStatus === 'succeeded' && job.result != null ? (
            <EvaluationRunResultView data={job.result} />
          ) : evalStatus === 'succeeded' && job.result == null ? (
            <p className="evaluator-params__hint">Evaluation succeeded but no result payload was returned.</p>
          ) : null}

          {!isActiveEvaluationJobStatus(evalStatus) &&
          evalStatus !== 'succeeded' &&
          evalStatus !== 'failed' &&
          evalStatus !== 'cancelled' ? (
            <p className="evaluator-params__hint">
              {progressMeta?.message || 'Waiting for evaluation updates…'}
            </p>
          ) : null}
        </>
      )}
    </TuningModalShell>
  );
};

export default EvaluationJobDetailsModal;
